import { useState } from "react";
import { Info, Loader2, X } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface CreateVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentVersion: number;
  onConfirm: (data: { versionNote: string; makeDefault: boolean }) => void;
  isPending?: boolean;
}

export function CreateVersionDialog({
  open,
  onOpenChange,
  currentVersion,
  onConfirm,
  isPending = false,
}: CreateVersionDialogProps) {
  const { t } = useLingui();
  const [versionNote, setVersionNote] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);

  const nextVersion = currentVersion + 1;
  const nextVersionLabel = `V ${nextVersion}.0`;
  const currentVersionLabel = `V ${currentVersion}.0`;

  function handleConfirm() {
    onConfirm({ versionNote, makeDefault });
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      setVersionNote("");
      setMakeDefault(false);
    }
    onOpenChange(open);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[700px] p-0 gap-0 overflow-hidden rounded-xl border border-(--shell-border)"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-(--color-border) p-5">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
              {t`Create new version`}
            </DialogTitle>
            <p className="text-base text-(--color-text-secondary)">
              {t`A new version of this recipe will be created based on ${currentVersionLabel}. The current version will remain saved in version history.`}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-(--color-text-secondary) hover:text-(--color-text-primary)"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            <X className="size-6" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-5 p-5">
          {/* Version ID (read-only) */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-(--color-text-primary)">
              {t`Version ID`}
            </label>
            <div className="flex h-[44px] items-center rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-4 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
              <span className="text-sm text-(--color-text-secondary)">{nextVersionLabel}</span>
            </div>
          </div>

          {/* Version note */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-(--color-text-primary)">
              {t`What changed in this version?`}
            </label>
            <Textarea
              value={versionNote}
              onChange={(e) => setVersionNote(e.target.value)}
              placeholder={t`Describe the changes...`}
              className="h-[140px] resize-none rounded-lg border-(--color-border) shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-sm text-(--color-text-primary)"
            />
            <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              <Info className="size-5 shrink-0" />
              <span>
                {t`Describe what was updated compared to ${currentVersionLabel} (ingredients, quantities, process, etc.)`}
              </span>
            </div>
          </div>

          {/* Make default toggle */}
          <div className="flex gap-3">
            <Switch
              checked={makeDefault}
              onCheckedChange={setMakeDefault}
              className="mt-0.5 shrink-0"
            />
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-(--color-text-primary)">
                {t`Make this version the default`}
              </span>
              <span className="text-sm text-(--color-text-secondary)">
                {t`When published, this will become the default version for all regions and locations.`}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 items-center justify-end border-t border-(--color-border) p-5">
          <Button
            variant="outline"
            className="w-[200px] h-[52px] rounded-[10px] border-(--color-border) text-base font-medium text-(--color-text-primary)"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            {t`Cancel`}
          </Button>
          <Button
            className="w-[200px] h-[52px] rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t`Create new version`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
